import { app } from "@microsoft/teams-js";

/**
 * Central Teams SDK bootstrap.
 *
 * This is the ONLY place in the whole app allowed to call `app.initialize()`
 * directly. Every other module (axios, SignalR, components) must use
 * `initializeTeamsApp()` and await its resolution before using any Teams SDK API.
 */

let initPromise: Promise<void> | null = null;

// Per-attempt timeout. `app.initialize()` never resolves when the app is not
// hosted by Teams (e.g. opened as a standalone tab via "Go to website"), so we
// bound each attempt ourselves to avoid the UI spinning forever.
const INIT_ATTEMPT_TIMEOUT_MS = 3000;

// Backoff between attempts inside a host (iframe), where the Teams Web handshake
// can race with page load. Outside an iframe a single attempt is enough.
const retryDelaysMs = [0, 1000, 2000];

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new Error("[Teams SDK] initialize timed out")),
      ms
    );
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      }
    );
  });
}

async function initializeWithRetry(): Promise<void> {
  let lastError: unknown;

  // Retries help only inside a host (iframe) where the handshake may race with
  // load. Outside an iframe (standalone tab / "Go to website") a single bounded
  // attempt keeps time-to-WarningPage short. Teams mobile initializes via a
  // native bridge and succeeds on the first attempt, so it is unaffected.
  const delays = isRunningInIframe() ? retryDelaysMs : [0];

  for (const retryDelay of delays) {
    if (retryDelay > 0) {
      await delay(retryDelay);
    }

    try {
      if (app.isInitialized()) {
        return;
      }

      await withTimeout(app.initialize(), INIT_ATTEMPT_TIMEOUT_MS);
      return;
    } catch (error) {
      lastError = error;
      console.warn("[Teams SDK] initialize failed", {
        error,
        referrer: document.referrer,
        href: window.location.href,
        isIframe: isRunningInIframe(),
      });
    }
  }

  throw lastError;
}

/**
 * Singleton Teams SDK initialization. Multiple concurrent consumers await the
 * same Promise, so there are never multiple parallel `app.initialize()` calls.
 * After a final failure `initPromise` is reset so a controlled retry is possible
 * (`retryInitializeTeamsApp`).
 */
export function initializeTeamsApp(): Promise<void> {
  if (app.isInitialized()) {
    return Promise.resolve();
  }

  if (!initPromise) {
    initPromise = initializeWithRetry().catch((error) => {
      initPromise = null;
      throw error;
    });
  }

  return initPromise;
}

/**
 * Returns whether the Teams SDK has already been initialized.
 */
export function isTeamsInitialized(): boolean {
  return app.isInitialized();
}

/**
 * Resets the local `initPromise` — ONLY when the SDK is not yet initialized.
 * Does not attempt to "de-initialize" the Teams SDK when
 * `app.isInitialized() === true` (the SDK does not support de-initialization).
 */
export function resetTeamsBootstrap(): void {
  if (!app.isInitialized()) {
    initPromise = null;
  }
}

/**
 * Controlled re-initialization attempt — used by the "Try again" button
 * in the technical error view.
 */
export function retryInitializeTeamsApp(): Promise<void> {
  resetTeamsBootstrap();
  return initializeTeamsApp();
}

/**
 * Whether the app runs inside an iframe.
 * Accessing `window.top` may throw on cross-origin — we treat that as
 * "we are inside an iframe".
 */
export function isRunningInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/**
 * Heuristic: whether the app is likely hosted by Teams / M365.
 *
 * NOTE: the result is used SOLELY for a UI decision (technical-error vs
 * WarningPage). It must never be used for authorization, security or backend
 * decisions.
 */
export function isLikelyTeamsHost(): boolean {
  const referrer = document.referrer || "";

  if (!isRunningInIframe()) {
    return false;
  }

  // Empty referrer inside an iframe → cautiously treat as a likely Teams host.
  // NOTE: this could be any iframe, not only Teams — hence the heuristic is
  // used for UI only, never for auth/security.
  if (!referrer) {
    return true;
  }

  return (
    referrer.includes("teams.microsoft.com") ||
    referrer.includes("teams.cloud.microsoft") ||
    referrer.includes("office.com") ||
    referrer.includes("microsoft365.com") ||
    referrer.includes("outlook.office.com")
  );
}
